import { Input, NativeSelect, Skeleton, Stack, Tabs, Text, useDisclosure } from '@chakra-ui/react';
import type { DataTableColumnDef as ColumnDef } from 'src/components/DataTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Donor } from 'src/api/Donor';
import { DonorKind } from 'src/api/DonorKind';
import { DonorListQuery } from 'src/api/DonorListQuery';
import { Status } from 'src/api/Status';
import AddButton from 'src/components/buttons/AddButton';
import ConfirmDialog from 'src/components/ConfirmDialog';
import Content from 'src/components/Content';
import DataTable, { DataTableToolbar } from 'src/components/DataTable';
import { DropDownMenu } from 'src/components/DropDownMenu';
import ErrorMessage from 'src/components/ErrorMessage';
import FeatureLocked from 'src/components/FeatureLocked';
import { useI18n } from 'src/i18n/I18nContext';
import WaitingSpinner from 'src/components/WaitingSpinner';
import { Button } from 'src/components/ui/button';
import { donorService } from 'src/service/DonorService';
import { toaster } from '@/components/ui/toaster';

const PAGE_SIZE = 25;

const donorKinds = [
    'person', 'household', 'company', 'foundation', 'church', 'municipality',
    'public-institution', 'association', 'other',
] as const;

const donorKindMessageKey = (kind: string): string =>
    `donors.form.kind.options.${kind === 'public-institution' ? 'publicInstitution' : kind}`;

const donorName = (donor: Donor, unnamed = 'Ohne Namen'): string =>
    donor.organizationName || [donor.firstName, donor.lastName].filter(Boolean).join(' ') || unnamed;

const OverviewDonors = () => {
    const { t } = useI18n();
    const [activeTab, setActiveTab] = useState('donors');
    const [data, setData] = useState<Donor[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState('');
    const [status, setStatus] = useState('active');
    const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt'>('updatedAt');
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('descending');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [archiveTarget, setArchiveTarget] = useState<Donor | null>(null);
    const { open, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();

    const query = useMemo<DonorListQuery>(() => ({
        search: search.trim() || null,
        kinds: kind ? [kind as DonorKind] : [],
        statuses: status ? [status as Status] : [],
        sortBy,
        sortDirection: sortDirection as DonorListQuery['sortDirection'],
        page,
        pageSize: PAGE_SIZE,
    }), [kind, page, search, sortBy, sortDirection, status]);

    const load = useCallback(() => {
        setIsLoading(true);
        setError('');
        return donorService.list(query)
            .then(result => {
                setData([...result.results]);
                setTotal(result.total);
                setPage(current => Math.min(current, Math.max(1, Math.ceil(result.total / PAGE_SIZE))));
            })
            .catch(() => setError(t('donors.overview.loadError')))
            .finally(() => setIsLoading(false));
    }, [query, t]);

    useEffect(() => { void load(); }, [load]);
    useEffect(() => { setPage(1); }, [search, kind, status, sortBy, sortDirection]);

    const archive = (): Promise<void> => {
        if (!archiveTarget) return Promise.resolve();
        return donorService.archive(archiveTarget.id).then(() => {
            toaster.create({ title: t('donors.overview.archived'), type: 'success' });
            setArchiveTarget(null);
            return load();
        });
    };

    const columns = useMemo<ColumnDef<Donor>[]>(() => [
        { id: 'name', header: t('donors.overview.columns.name'), accessorFn: row => donorName(row, t('donors.overview.unnamed')), cell: info => <Text fontWeight='bold'>{info.getValue() as string}</Text> },
        { id: 'kind', header: t('donors.overview.columns.kind'), accessorKey: 'kind', cell: info => t(donorKindMessageKey(String(info.getValue()))) },
        { id: 'place', header: t('donors.overview.columns.place'), accessorFn: row => [row.city, row.countryCode].filter(Boolean).join(', ') || '–' },
        { id: 'contact', header: t('donors.overview.columns.contact'), accessorFn: row => row.primaryEmail || row.primaryPhone || '–' },
        { id: 'source', header: t('donors.overview.columns.source'), accessorFn: row => row.sourceSystems.join(', ') || t('donors.overview.manual') },
        { id: 'status', header: t('donors.overview.columns.status'), accessorKey: 'status', cell: info => t(`donors.overview.status.${info.getValue() as Status}`) },
        {
            id: 'actions', enableSorting: false,
            cell: info => <DropDownMenu
                onMenuView={() => navigate(`/fundraising/donors/view/${info.row.original.id}`)}
                onMenuEdit={() => navigate(`/fundraising/donors/edit/${info.row.original.id}`)}
                onMenuDelete={() => { setArchiveTarget(info.row.original); onOpen(); }}
                deleteCaption={t('donors.overview.archive')}
            />,
        },
    ], [navigate, onOpen, t]);

    return (
        <Content
            title={t('donors.tabs.overview')}
            description={t('donors.overview.description')}
            actions={activeTab === 'donors' && <AddButton caption={t('donors.overview.create')} onClick={() => navigate('/fundraising/donors/add')} />}
        >
            <Tabs.Root value={activeTab} onValueChange={({ value }) => setActiveTab(value)} lazyMount>
                <Tabs.List>
                    <Tabs.Trigger value='donors'>
                        {t('donors.tabs.overview')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value='recurringPayments'>
                        {t('donors.tabs.recurringPayments')}
                    </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value='donors'>
                    <Stack gap='6'>
                        <DataTableToolbar
                            resultCount={total}
                            filtersActive={search.trim() !== '' || kind !== '' || status !== ''}
                            onClearFilters={() => {
                                setSearch('');
                                setKind('');
                                setStatus('');
                            }}>
                            <Input aria-label={t('donors.overview.filters.search')} placeholder={t('donors.overview.filters.search')} value={search} onChange={event => setSearch(event.currentTarget.value)} w={{ base: 'full', md: '20rem' }} maxW='full' />
                            <NativeSelect.Root width={{ base: 'full', md: '12rem' }}><NativeSelect.Field aria-label={t('donors.overview.filters.kind')} value={kind} onChange={event => setKind(event.currentTarget.value)}>
                                <option value=''>{t('donors.overview.filters.allKinds')}</option>{donorKinds.map(value => <option key={value} value={value}>{t(donorKindMessageKey(value))}</option>)}
                            </NativeSelect.Field></NativeSelect.Root>
                            <NativeSelect.Root width={{ base: 'full', md: '10rem' }}><NativeSelect.Field aria-label={t('donors.overview.filters.status')} value={status} onChange={event => setStatus(event.currentTarget.value)}>
                                <option value='active'>{t('donors.overview.status.active')}</option><option value='archived'>{t('donors.overview.status.archived')}</option><option value=''>{t('donors.overview.filters.allStatuses')}</option>
                            </NativeSelect.Field></NativeSelect.Root>
                            <NativeSelect.Root width={{ base: 'full', md: '10rem' }}><NativeSelect.Field aria-label={t('donors.overview.sort.field')} value={sortBy} onChange={event => setSortBy(event.currentTarget.value as typeof sortBy)}>
                                <option value='updatedAt'>{t('donors.overview.sort.updatedAt')}</option><option value='createdAt'>{t('donors.overview.sort.createdAt')}</option>
                            </NativeSelect.Field></NativeSelect.Root>
                            <NativeSelect.Root width={{ base: 'full', md: '11rem' }}><NativeSelect.Field aria-label={t('donors.overview.sort.direction')} value={sortDirection} onChange={event => setSortDirection(event.currentTarget.value as typeof sortDirection)}>
                                <option value='descending'>{t('donors.overview.sort.descending')}</option><option value='ascending'>{t('donors.overview.sort.ascending')}</option>
                            </NativeSelect.Field></NativeSelect.Root>
                        </DataTableToolbar>
                        {error && <ErrorMessage message={error} />}
                        {isLoading && data.length === 0 ? <Stack py={5}><WaitingSpinner size='xl' /><Skeleton height='20px' /><Skeleton height='20px' /></Stack> : data.length === 0 ? (
                            <Stack py={10} align='center'><Text>{t('donors.overview.empty')}</Text><Button onClick={() => navigate('/fundraising/donors/add')}>{t('donors.overview.createFirst')}</Button></Stack>
                        ) : <Stack aria-busy={isLoading} opacity={isLoading ? 0.6 : 1}>
                            <DataTable mode='server' columns={columns} data={data}
                                pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage, loading: isLoading }}
                                hideColumnsOnXs={['kind', 'place', 'source']}
                                onRowClick={row => navigate(`/fundraising/donors/view/${row.id}`)} />
                        </Stack>}
                    </Stack>
                </Tabs.Content>
                <Tabs.Content value='recurringPayments'>
                    <FeatureLocked />
                </Tabs.Content>
            </Tabs.Root>
            <ConfirmDialog heading={t('donors.overview.archiveHeading', { name: archiveTarget ? donorName(archiveTarget, t('donors.overview.unnamed')) : t('donors.tabs.overview') })} isOpen={open} onClose={onClose} onConfirm={archive} />
        </Content>
    );
};

export default OverviewDonors;
